/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import type { Agent } from '@shared/schema/agents';
import type { SystemAgentRun, SystemAgentSchedule, FindingsSummary, SystemAgentMetadata, SystemAgentFinding, FindingSeverity, FindingStatus } from '@shared/schema/systemAgents';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ActiveProductIndicator } from '@/components/common/ActiveProductIndicator';
import { AgentCard, AgentFocusView, AgentsMdTab, ChatAgentsTab } from '../components/agents';
import {
  listSystemAgents,
  listSystemAgentRuns,
  listSystemAgentSchedules,
  getSystemAgentFindingsSummary,
  triggerSystemAgentRun,
  cancelSystemAgentRun,
  listSystemAgentFindings,
  updateSystemAgentFinding,
} from '../lib/api/system-agents';
import {
  listAgentExecutions,
  agentExecutionToUnifiedRun,
  systemAgentRunToUnifiedRun,
  dsaRunToUnifiedRun,
  type UnifiedRun,
  type UnifiedRunType,
} from '../lib/api/agent-executions';
import { terminateAgentExecution } from '@shared/lib/api/review';
import { getDSARuns, getDSAStatus, type DSAStatusResult } from '../lib/api/dsa';
import type { OrganizationIntegrationSettings, DSAOrgSettings } from '@shared/types/organizationSettings';
import type { DSARun } from '@shared/schema/dsa';
import {
  Bot,
  RefreshCw,
  Loader2,
  Plus,
  LayoutGrid,
  List,
  Search,
  AlertTriangle,
  Package,
  Building2,
  PlayCircle,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileWarning,
  Filter,
  Wrench,
  FileText,
  FileCode,
  Check,
  X,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';

type ViewMode = 'grid' | 'list';
type AgentsTab = 'product' | 'organization' | 'findings' | 'runs' | 'chat-agents' | 'agents-md';

export function Agents() {
  const navigate = useNavigate();
  const { activeProduct } = useApp();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [runs, setRuns] = useState<SystemAgentRun[]>([]);
  const [agentExecutions, setAgentExecutions] = useState<Awaited<ReturnType<typeof listAgentExecutions>>['executions']>([]);
  const [dsaRuns, setDsaRuns] = useState<DSARun[]>([]);
  const [dsaStatus, setDsaStatus] = useState<DSAStatusResult | null>(null);
  const [orgDSASettings, setOrgDSASettings] = useState<DSAOrgSettings | null>(null);
  const [schedules, setSchedules] = useState<SystemAgentSchedule[]>([]);
  const [runsTypeFilter, setRunsTypeFilter] = useState<UnifiedRunType | 'all'>('all');
  const [findings, setFindings] = useState<SystemAgentFinding[]>([]);
  const [findingsSummary, setFindingsSummary] = useState<FindingsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFindingsLoading, setIsFindingsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [runningAgents, setRunningAgents] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<AgentsTab>('product');
  // Findings filters
  // Scope determines which findings file to read from:
  // - 'product': reads from active product's VPS path (product-specific findings)
  // - 'organization': reads from org-wide DSA path
  const [findingsScope, setFindingsScope] = useState<'product' | 'organization'>('product');
  const [findingSeverityFilter, setFindingSeverityFilter] = useState<FindingSeverity | 'all'>('all');
  const [findingStatusFilter, setFindingStatusFilter] = useState<FindingStatus | 'all'>('all');
  const [findingProductFilter, setFindingProductFilter] = useState<string>('all');
  const [findingAgentFilter, setFindingAgentFilter] = useState<string>('all');
  const { toast } = useToast();

  // Fetch all data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // For findings summary, pass productId when scope is 'product' to read from product-specific VPS path
      const summaryOptions: { status: 'open'; productId?: string } = { status: 'open' };
      if (findingsScope === 'product' && activeProduct?.id) {
        summaryOptions.productId = activeProduct.id;
      }
      
      const [agentsResult, runsResult, executionsResult, dsaRunsResult, schedulesResult, summaryResult, dsaStatusResult, orgSettingsResponse] = await Promise.all([
        listSystemAgents(),
        listSystemAgentRuns({ limit: 100 }),
        listAgentExecutions({ limit: 100 }),
        getDSARuns(),
        listSystemAgentSchedules(),
        getSystemAgentFindingsSummary(summaryOptions),
        getDSAStatus(),
        // Fetch org settings directly to get org-level DSA configuration
        fetch('/api/settings/organization').then(res => res.ok ? res.json() : null).catch(() => null),
      ]);

      if (agentsResult.success && agentsResult.agents) {
        setAgents(agentsResult.agents);
      }
      if (runsResult.success && runsResult.runs) {
        setRuns(runsResult.runs);
        // Track running agents
        const running = new Set<number>();
        runsResult.runs.forEach((run: SystemAgentRun) => {
          if (run.status === 'running' || run.status === 'pending') {
            running.add(run.agentId);
          }
        });
        setRunningAgents(running);
      }
      if (executionsResult.success && executionsResult.executions) {
        setAgentExecutions(executionsResult.executions);
      } else {
        setAgentExecutions([]);
      }
      if (dsaRunsResult.success && dsaRunsResult.runs) {
        const allDsa = [...(dsaRunsResult.runs.active ?? []), ...(dsaRunsResult.runs.completed ?? [])];
        allDsa.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
        setDsaRuns(allDsa);
      } else {
        setDsaRuns([]);
      }
      if (schedulesResult.success && schedulesResult.schedules) {
        setSchedules(schedulesResult.schedules);
      }
      if (summaryResult.success && summaryResult.summary) {
        setFindingsSummary(summaryResult.summary);
      }
      setDsaStatus(dsaStatusResult);
      
      // Extract org-level DSA settings from API response
      if (orgSettingsResponse?.data) {
        const integrationSettings = orgSettingsResponse.data.integrationSettings as OrganizationIntegrationSettings | undefined;
        const dsaSettings = integrationSettings?.dsa as DSAOrgSettings | undefined;
        setOrgDSASettings(dsaSettings || null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load system agents. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, findingsScope, activeProduct?.id]);

  // Merged runs for Runs tab: system agent runs + agent executions + DSA runs, sorted by date desc
  const mergedRuns: UnifiedRun[] = (() => {
    const systemRuns: UnifiedRun[] = runs.map((run) => {
      const agent = agents.find((a) => a.id === run.agentId);
      const agentName = agent?.displayName || agent?.name || `Agent ${run.agentId}`;
      return systemAgentRunToUnifiedRun(run, agentName);
    });
    const executionRuns: UnifiedRun[] = (agentExecutions ?? []).map(agentExecutionToUnifiedRun);
    const dsaRunsMapped: UnifiedRun[] = dsaRuns.map(dsaRunToUnifiedRun);
    const combined = [...systemRuns, ...executionRuns, ...dsaRunsMapped];
    combined.sort((a, b) => {
      const aTime = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const bTime = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return bTime - aTime;
    });
    return combined;
  })();

  const filteredRuns = runsTypeFilter === 'all'
    ? mergedRuns
    : mergedRuns.filter((r) => r.runType === runsTypeFilter);

  // Poll runs list while Runs tab is visible and there are active runs
  const hasActiveRuns = mergedRuns.some((r) => r.status === 'running' || r.status === 'pending');
  const runTypeFilterOptions: { value: UnifiedRunType | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'system_agent', label: 'System agents' },
    { value: 'agent_execution', label: 'Sidebar & chat' },
    { value: 'grooming', label: 'Grooming' },
    { value: 'code_generation', label: 'Code gen' },
    { value: 'quick_request', label: 'Quick request' },
    { value: 'dsa', label: 'DSA' },
  ];
  useEffect(() => {
    if (activeTab !== 'runs' || !hasActiveRuns) return;
    const interval = setInterval(() => fetchData(), 4000);
    return () => clearInterval(interval);
  }, [activeTab, hasActiveRuns, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch findings with current filters
  // Scope determines where to read findings from:
  // - 'product': reads from active product's VPS path (product-specific findings)
  // - 'organization': reads from org-wide DSA path
  const fetchFindings = useCallback(async () => {
    setIsFindingsLoading(true);
    try {
      const options: any = { limit: 100 };
      if (findingSeverityFilter !== 'all') options.severity = findingSeverityFilter;
      if (findingStatusFilter !== 'all') options.status = findingStatusFilter;
      if (findingAgentFilter !== 'all') options.agentId = parseInt(findingAgentFilter);
      
      // When scope is 'product', pass the activeProduct ID to read from product-specific VPS path
      // When scope is 'organization', don't pass productId to read from org-wide DSA path
      if (findingsScope === 'product' && activeProduct?.id) {
        options.productId = activeProduct.id;
      }

      const result = await listSystemAgentFindings(options);
      if (result.success && result.findings) {
        setFindings(result.findings);
      }
    } catch (error) {
      console.error('Error fetching findings:', error);
    } finally {
      setIsFindingsLoading(false);
    }
  }, [findingSeverityFilter, findingStatusFilter, findingAgentFilter, findingsScope, activeProduct?.id]);

  // Fetch findings when tab changes to findings or filters change
  useEffect(() => {
    if (activeTab === 'findings') {
      fetchFindings();
    }
  }, [activeTab, fetchFindings]);

  // Get last run info for an agent
  const getAgentRunInfo = (agentId: number) => {
    const agentRuns = runs.filter(r => r.agentId === agentId);
    const lastRun = agentRuns[0]; // Assuming sorted by date desc
    const runDate = lastRun?.completedAt || lastRun?.startedAt;
    return {
      lastRunAt: runDate ? (typeof runDate === 'string' ? runDate : runDate.toISOString()) : undefined,
      lastRunResult: lastRun?.result,
      isRunning: runningAgents.has(agentId),
    };
  };

  // Get schedule for an agent
  const getAgentSchedule = (agentId: number) => {
    const schedule = schedules.find(s => s.agentId === agentId && s.enabled);
    return schedule?.cronExpression;
  };

  // Get findings count for an agent
  const getAgentFindingsCount = (agentId: number) => {
    if (!findingsSummary?.byAgent) return 0;
    // The summary might use agent name as key, try both ID and name
    const agent = agents.find(a => a.id === agentId);
    return findingsSummary.byAgent[agentId.toString()] || 
           findingsSummary.byAgent[agent?.name || ''] || 
           0;
  };

  // Handle running an agent
  // When scope is 'product', run only against the active product
  // When scope is 'organization', run against all products (org-wide)
  const handleRunAgent = async (agent: Agent, scope: 'product' | 'organization' = 'product') => {
    setRunningAgents(prev => new Set(prev).add(agent.id));
    try {
      // For product scope, pass the active product ID to limit execution to that product
      // For organization scope, don't pass targetProducts so it runs against all products
      const options = scope === 'product' && activeProduct?.id 
        ? { targetProducts: [activeProduct.id] }
        : undefined;
      
      const result = await triggerSystemAgentRun(agent.id, options);
      if (result.success) {
        toast({
          title: 'Agent run started',
          description: `${agent.displayName || agent.name} is now running${scope === 'product' && activeProduct ? ` on ${activeProduct.title}` : ' across all products'}.`,
        });
        // Refresh data after a short delay
        setTimeout(fetchData, 1000);
      } else {
        toast({
          title: 'Failed to start run',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
        setRunningAgents(prev => {
          const next = new Set(prev);
          next.delete(agent.id);
          return next;
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to trigger agent run.',
        variant: 'destructive',
      });
      setRunningAgents(prev => {
        const next = new Set(prev);
        next.delete(agent.id);
        return next;
      });
    }
  };

  // Total open findings
  const totalOpenFindings = findingsSummary?.total || 0;
  const criticalFindings = findingsSummary?.bySeverity?.critical || 0;

  // If an agent is selected, show the focus view
  if (selectedAgent) {
    return (
      <div className="container py-6">
        <AgentFocusView
          agent={selectedAgent}
          activeProductId={activeProduct?.id}
          onBack={() => setSelectedAgent(null)}
          onRunTriggered={fetchData}
        />
      </div>
    );
  }

  // Helper to format relative time
  const formatRelativeTime = (dateStr: string | Date | null | undefined) => {
    if (!dateStr) return 'Never';
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get status icon for a run
  const getRunStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'running':
      case 'pending':
        return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'cancelled':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Get result badge color
  const getResultBadgeVariant = (result: string | undefined) => {
    switch (result) {
      case 'pass':
        return 'default';
      case 'warning':
        return 'secondary';
      case 'fail':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Render Product Tab Content (existing System Agents grid)
  const renderProductTab = () => (
    <>
      {/* Summary Banner */}
      {totalOpenFindings > 0 && (
        <Card className={`mb-6 ${criticalFindings > 0 ? 'border-red-300 bg-red-50' : 'border-yellow-300 bg-yellow-50'}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`h-5 w-5 ${criticalFindings > 0 ? 'text-red-600' : 'text-yellow-600'}`} />
                <div>
                  <p className={`font-medium ${criticalFindings > 0 ? 'text-red-900' : 'text-yellow-900'}`}>
                    {totalOpenFindings} open finding{totalOpenFindings !== 1 ? 's' : ''} require attention
                  </p>
                  <p className={`text-sm ${criticalFindings > 0 ? 'text-red-700' : 'text-yellow-700'}`}>
                    {criticalFindings > 0 
                      ? `${criticalFindings} critical issue${criticalFindings !== 1 ? 's' : ''} found`
                      : 'Review and address warnings to maintain code quality'}
                  </p>
                </div>
              </div>
              <Button 
                variant={criticalFindings > 0 ? 'destructive' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('findings')}
              >
                View All Findings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Toggle */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {agents.length} agent{agents.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading agents...</p>
          </div>
        </div>
      ) : agents.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Bot className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Product Agents</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              Product agents provide automated oversight for your local product codebases. 
              Configure agents to monitor security, test coverage, and more.
            </p>
          </div>
        </div>
      ) : (
        /* Agent Grid/List */
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
            : 'space-y-4'
        }>
          {agents.map((agent) => {
            const runInfo = getAgentRunInfo(agent.id);
            const schedule = getAgentSchedule(agent.id);
            const findingsCount = getAgentFindingsCount(agent.id);

            return (
              <AgentCard
                key={agent.id}
                agent={agent}
                lastRunAt={runInfo.lastRunAt ?? undefined}
                lastRunResult={runInfo.lastRunResult ?? undefined}
                findingsCount={findingsCount}
                schedule={schedule}
                isRunning={runInfo.isRunning}
                onClick={() => setSelectedAgent(agent)}
                onRunNow={() => handleRunAgent(agent, 'product')}
              />
            );
          })}
        </div>
      )}
    </>
  );

  // Render Organization Tab Content (DSA Agents)
  // Shows the same agents as Product tab, but runs them with org-wide scope when DSA is enabled
  const renderOrganizationTab = () => {
    // Check org-level DSA settings (not local worker status)
    const isDSAEnabledOrgWide = orgDSASettings?.enabled ?? false;
    const velocityRepoConfigured = !!orgDSASettings?.velocityRepoUrl;
    
    // If DSA is not enabled at org level or Velocity repo is not configured, show setup message
    if (!isDSAEnabledOrgWide || !velocityRepoConfigured) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Building2 className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Distributed System Agents</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              Organization-wide agents run across distributed infrastructure using the Velocity Repo 
              for coordination. Configure DSA settings to enable organization-level oversight.
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              {!isDSAEnabledOrgWide 
                ? 'DSA is not enabled for your organization.'
                : 'Velocity repository is not configured.'}
            </p>
            <Button variant="outline" onClick={() => window.location.href = '/settings?tab=organization'}>
              Configure DSA Settings
            </Button>
          </div>
        </div>
      );
    }
    
    // DSA is enabled - show the same agents grid but with org-wide scope for running
    return (
      <>
        {/* Info banner about org-wide execution */}
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-purple-600" />
              <div>
                <p className="font-medium text-purple-900">Organization-Wide Execution</p>
                <p className="text-sm text-purple-700">
                  Agents run here will analyze all products in your organization. Findings are stored in the organization's Velocity repository.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {agents.length} agent{agents.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">Loading agents...</p>
            </div>
          </div>
        ) : agents.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Bot className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Organization Agents</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                Organization agents provide automated oversight across all products. 
                Configure agents to monitor security, test coverage, and more.
              </p>
            </div>
          </div>
        ) : (
          /* Agent Grid/List - same as Product tab but with 'organization' scope */
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'space-y-4'
          }>
            {agents.map((agent) => {
              const runInfo = getAgentRunInfo(agent.id);
              const schedule = getAgentSchedule(agent.id);
              const findingsCount = getAgentFindingsCount(agent.id);

              return (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  lastRunAt={runInfo.lastRunAt ?? undefined}
                  lastRunResult={runInfo.lastRunResult ?? undefined}
                  findingsCount={findingsCount}
                  schedule={schedule}
                  isRunning={runInfo.isRunning}
                  onClick={() => setSelectedAgent(agent)}
                  onRunNow={() => handleRunAgent(agent, 'organization')}
                />
              );
            })}
          </div>
        )}
      </>
    );
  };

  // Get navigation info for a run based on its type and metadata
  const getRunNavigationInfo = (run: UnifiedRun): { label: string; onClick: () => void } | null => {
    const metadata = run.metadata as Record<string, unknown> | null;
    
    // For grooming runs - can have storyId, epicId, or issueId
    if (run.runType === 'grooming' && metadata) {
      if (metadata.storyId) {
        return {
          label: 'View Story',
          onClick: () => navigate(`/user-stories#${metadata.storyId}`),
        };
      }
      if (metadata.epicId) {
        return {
          label: 'View Epic',
          onClick: () => navigate(`/user-stories#${metadata.epicId}`),
        };
      }
      if (metadata.issueId) {
        return {
          label: 'View Issue',
          onClick: () => navigate(`/issues#${metadata.issueId}`),
        };
      }
    }
    
    // For code generation runs - link to issue
    if (run.runType === 'code_generation' && metadata) {
      const issueId = metadata.issueId ?? metadata.entityId;
      if (issueId) {
        return {
          label: 'View Issue',
          onClick: () => navigate(`/issues#${issueId}`),
        };
      }
    }
    
    // For system agent runs - link to findings tab
    if (run.runType === 'system_agent') {
      return {
        label: 'View Findings',
        onClick: () => setActiveTab('findings'),
      };
    }
    
    // For DSA runs - link to findings tab (DSA produces findings)
    if (run.runType === 'dsa') {
      return {
        label: 'View Findings',
        onClick: () => setActiveTab('findings'),
      };
    }
    
    return null;
  };

  // Cancel run handler (system agent or agent execution)
  const handleCancelRun = async (run: UnifiedRun) => {
    if (run.runType === 'system_agent' && run.metadata && 'systemRunId' in run.metadata) {
      const systemRunId = run.metadata.systemRunId as number;
      const result = await cancelSystemAgentRun(systemRunId);
      if (result.success) {
        toast({ title: 'Run cancelled', description: 'System agent run was cancelled.' });
        fetchData();
      } else {
        toast({ title: 'Cancel failed', description: result.error, variant: 'destructive' });
      }
      return;
    }
    if ((run.runType === 'agent_execution' || run.runType === 'grooming' || run.runType === 'code_generation' || run.runType === 'quick_request') && run.executionId) {
      try {
        await terminateAgentExecution(run.executionId, 'User requested cancel from Runs tab');
        toast({ title: 'Run cancelled', description: 'Execution was terminated.' });
        fetchData();
      } catch (e) {
        toast({ title: 'Cancel failed', description: e instanceof Error ? e.message : 'Failed to terminate', variant: 'destructive' });
      }
    }
  };

  // Render Runs Tab Content (Unified Agent Runs)
  const renderRunsTab = () => (
    <>
      {/* Runs Summary + Filter */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm text-muted-foreground">
            {mergedRuns.length} run{mergedRuns.length !== 1 ? 's' : ''} total
          </span>
          <span className="text-sm text-muted-foreground">
            {mergedRuns.filter((r) => r.status === 'running' || r.status === 'pending').length} active
          </span>
          <select
            value={runsTypeFilter}
            onChange={(e) => setRunsTypeFilter(e.target.value as UnifiedRunType | 'all')}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {runTypeFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Runs List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading runs...</p>
          </div>
        </div>
      ) : filteredRuns.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <PlayCircle className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Agent Runs</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              {runsTypeFilter === 'all'
                ? 'Agent runs will appear here when you trigger agents, use the sidebar chat, groom stories/issues, run code generation, or DSA.'
                : `No runs of type "${runTypeFilterOptions.find((o) => o.value === runsTypeFilter)?.label ?? runsTypeFilter}".`}
            </p>
            {runsTypeFilter !== 'all' && (
              <Button variant="outline" onClick={() => setRunsTypeFilter('all')}>
                Show all
              </Button>
            )}
            {runsTypeFilter === 'all' && (
              <Button variant="outline" onClick={() => setActiveTab('product')}>
                View Agents
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRuns.map((run) => {
            const isActive = run.status === 'running' || run.status === 'pending';
            const isSystemAgent = run.runType === 'system_agent';

            return (
              <Card key={run.id} className={isActive ? 'border-blue-200 bg-blue-50/50' : ''}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getRunStatusIcon(run.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{run.name}</span>
                          <Badge variant={getResultBadgeVariant(run.result ?? undefined)}>
                            {run.status === 'completed' ? (run.result || 'completed') : run.status}
                          </Badge>
                          {isSystemAgent && run.triggeredBy && (
                            <Badge variant="outline" className="text-xs">
                              {run.triggeredBy}
                            </Badge>
                          )}
                          {!isSystemAgent && (
                            <Badge variant="outline" className="text-xs">
                              {run.runType === 'grooming' ? 'Grooming' : run.runType === 'code_generation' ? 'Code gen' : run.runType === 'quick_request' ? 'Quick request' : run.runType === 'dsa' ? 'DSA' : 'Chat'}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span>
                            {isActive ? 'Started' : 'Completed'} {formatRelativeTime(isActive ? run.startedAt : run.completedAt)}
                          </span>
                          {run.durationSeconds != null && (
                            <span>Duration: {Math.floor(run.durationSeconds / 60)}m {run.durationSeconds % 60}s</span>
                          )}
                        </div>
                        {(run.summary || run.message) && (
                          <p className="text-sm mt-1 truncate max-w-xl" title={run.summary || run.message || undefined}>
                            {run.summary || (run.message && run.message.length > 120 ? `${run.message.slice(0, 120)}…` : run.message)}
                          </p>
                        )}
                        {(run.errorMessage) && (
                          <p className="text-sm text-red-600 mt-1">{run.errorMessage}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isActive && run.runType !== 'dsa' && (
                        <Button variant="outline" size="sm" onClick={() => handleCancelRun(run)}>
                          Cancel
                        </Button>
                      )}
                      {run.status === 'failed' && isSystemAgent && (
                        <Button variant="outline" size="sm">
                          Retry
                        </Button>
                      )}
                      {(() => {
                        const navInfo = getRunNavigationInfo(run);
                        return navInfo ? (
                          <Button variant="outline" size="sm" onClick={navInfo.onClick}>
                            <ExternalLink className="h-3 w-3 mr-1" />
                            {navInfo.label}
                          </Button>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );

  // Get severity badge styling
  const getSeverityBadgeStyle = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'info':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get status badge styling
  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'acknowledged':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'dismissed':
        return 'bg-gray-50 text-gray-600 border-gray-200';
      case 'fixed':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  // Handle finding status update
  const handleFindingStatusUpdate = async (findingId: string, newStatus: FindingStatus) => {
    try {
      const result = await updateSystemAgentFinding(findingId, { status: newStatus });
      if (result.success) {
        toast({
          title: 'Finding updated',
          description: `Status changed to ${newStatus}.`,
        });
        fetchFindings();
        fetchData(); // Refresh summary
      } else {
        toast({
          title: 'Failed to update',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update finding.',
        variant: 'destructive',
      });
    }
  };

  // Get unique products from findings summary
  const getUniqueProducts = () => {
    if (!findingsSummary?.byProduct) return [];
    return Object.keys(findingsSummary.byProduct);
  };

  // Render Findings Tab Content
  const renderFindingsTab = () => (
    <>
      {/* Scope Selector - determines which findings file to read from */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-muted/50 rounded-lg border">
        <span className="text-sm font-medium">Findings Source:</span>
        <div className="flex items-center gap-1 bg-background rounded-md p-1 border">
          <button
            onClick={() => setFindingsScope('product')}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              findingsScope === 'product'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <Package className="h-3.5 w-3.5 inline mr-1.5" />
            Product
          </button>
          <button
            onClick={() => setFindingsScope('organization')}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              findingsScope === 'organization'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <Building2 className="h-3.5 w-3.5 inline mr-1.5" />
            Organization
          </button>
        </div>
        <span className="text-xs text-muted-foreground">
          {findingsScope === 'product' 
            ? `Reading from ${activeProduct?.title || 'active product'}'s findings`
            : 'Reading from organization-wide DSA findings'}
        </span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters:</span>
        </div>

        {/* Severity Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Severity:</label>
          <select
            value={findingSeverityFilter}
            onChange={(e) => setFindingSeverityFilter(e.target.value as FindingSeverity | 'all')}
            className="text-sm border rounded px-2 py-1 bg-background"
          >
            <option value="all">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Status:</label>
          <select
            value={findingStatusFilter}
            onChange={(e) => setFindingStatusFilter(e.target.value as FindingStatus | 'all')}
            className="text-sm border rounded px-2 py-1 bg-background"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="dismissed">Dismissed</option>
            <option value="fixed">Fixed</option>
          </select>
        </div>

        {/* Product Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Product:</label>
          <select
            value={findingProductFilter}
            onChange={(e) => setFindingProductFilter(e.target.value)}
            className="text-sm border rounded px-2 py-1 bg-background"
          >
            <option value="all">All Products</option>
            {getUniqueProducts().map(product => (
              <option key={product} value={product}>{product}</option>
            ))}
          </select>
        </div>

        {/* Agent Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Agent:</label>
          <select
            value={findingAgentFilter}
            onChange={(e) => setFindingAgentFilter(e.target.value)}
            className="text-sm border rounded px-2 py-1 bg-background"
          >
            <option value="all">All Agents</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id.toString()}>
                {agent.displayName || agent.name}
              </option>
            ))}
          </select>
        </div>

        <Button variant="outline" size="sm" onClick={fetchFindings} disabled={isFindingsLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isFindingsLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      {findingsSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-red-700">{findingsSummary.bySeverity?.critical || 0}</p>
              <p className="text-xs text-red-600">Critical</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-orange-700">{findingsSummary.bySeverity?.high || 0}</p>
              <p className="text-xs text-orange-600">High</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-yellow-700">{findingsSummary.bySeverity?.medium || 0}</p>
              <p className="text-xs text-yellow-600">Medium</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{findingsSummary.bySeverity?.low || 0}</p>
              <p className="text-xs text-blue-600">Low</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200 bg-gray-50/50">
            <CardContent className="py-3 text-center">
              <p className="text-2xl font-bold text-gray-700">{findingsSummary.bySeverity?.info || 0}</p>
              <p className="text-xs text-gray-600">Info</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Findings Count */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">
          {findings.length} finding{findings.length !== 1 ? 's' : ''} 
          {findingSeverityFilter !== 'all' || findingStatusFilter !== 'all' || findingProductFilter !== 'all' || findingAgentFilter !== 'all' 
            ? ' (filtered)' 
            : ''}
        </span>
      </div>

      {/* Findings List */}
      {isFindingsLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading findings...</p>
          </div>
        </div>
      ) : findings.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <FileWarning className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Findings</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              {findingSeverityFilter !== 'all' || findingStatusFilter !== 'all' || findingProductFilter !== 'all' || findingAgentFilter !== 'all'
                ? 'No findings match your current filters. Try adjusting the filter criteria.'
                : 'No findings have been reported by system agents yet. Run an agent to scan your products.'}
            </p>
            <Button variant="outline" onClick={() => setActiveTab('product')}>
              View Agents
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {findings.map((finding) => {
            const agent = agents.find(a => a.id === finding.agentId);
            const agentName = agent?.displayName || agent?.name || `Agent ${finding.agentId}`;
            const isCriticalOrHigh = finding.severity === 'critical' || finding.severity === 'high';

            return (
              <Card 
                key={finding.findingId} 
                className={`${isCriticalOrHigh ? 'border-l-4 border-l-red-500' : ''}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Header with severity and title */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge className={`${getSeverityBadgeStyle(finding.severity)} uppercase text-xs font-semibold`}>
                          {finding.severity}
                        </Badge>
                        <span className="font-medium text-base">{finding.title}</span>
                        <Badge className={`${getStatusBadgeStyle(finding.status || 'open')} text-xs`}>
                          {finding.status || 'open'}
                        </Badge>
                      </div>

                      {/* Metadata row */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2 flex-wrap">
                        {finding.productId && (
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {finding.productId}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Bot className="h-3 w-3" />
                          {agentName}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(finding.createdAt)}
                        </span>
                      </div>

                      {/* File path */}
                      {finding.filePath && (
                        <div className="text-sm text-muted-foreground mb-2 font-mono bg-muted/50 px-2 py-1 rounded inline-flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {finding.filePath}
                          {finding.lineNumber && `:${finding.lineNumber}`}
                        </div>
                      )}

                      {/* Description */}
                      {finding.description && (
                        <p className="text-sm text-muted-foreground mb-2">{finding.description}</p>
                      )}

                      {/* Code snippet */}
                      {finding.codeSnippet && (
                        <pre className="text-xs bg-muted p-2 rounded font-mono overflow-x-auto mb-2 max-h-24">
                          {finding.codeSnippet}
                        </pre>
                      )}

                      {/* Recommendation */}
                      {finding.recommendation && (
                        <div className="text-sm bg-blue-50 text-blue-800 p-2 rounded border border-blue-200">
                          <span className="font-medium">Recommendation:</span> {finding.recommendation}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 shrink-0">
                      {finding.status === 'open' && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-xs"
                            onClick={() => handleFindingStatusUpdate(finding.findingId, 'acknowledged')}
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Acknowledge
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-xs"
                            onClick={() => handleFindingStatusUpdate(finding.findingId, 'dismissed')}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Dismiss
                          </Button>
                        </>
                      )}
                      {finding.status === 'acknowledged' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-xs"
                          onClick={() => handleFindingStatusUpdate(finding.findingId, 'fixed')}
                        >
                          <Wrench className="h-3 w-3 mr-1" />
                          Mark Fixed
                        </Button>
                      )}
                      {(finding.status === 'dismissed' || finding.status === 'fixed') && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-xs"
                          onClick={() => handleFindingStatusUpdate(finding.findingId, 'open')}
                        >
                          Reopen
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className="container py-6">
      <div className="card">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Bot className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-semibold">Agents</h1>
                <p className="text-sm text-muted-foreground">
                  Automated oversight and analysis for your products
                </p>
              </div>
            </div>
            <div className="h-8 w-px bg-border" />
            <ActiveProductIndicator />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '24px',
          gap: '4px'
        }}>
          <button
            onClick={() => setActiveTab('product')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'product' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'product' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <Package size={16} />
            Product
          </button>
          <button
            onClick={() => setActiveTab('organization')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'organization' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'organization' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <Building2 size={16} />
            Organization
          </button>
          <button
            onClick={() => setActiveTab('findings')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'findings' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'findings' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
          >
            <FileWarning size={16} />
            Findings
            {totalOpenFindings > 0 && (
              <span style={{
                backgroundColor: criticalFindings > 0 ? '#dc2626' : '#eab308',
                color: 'white',
                fontSize: '10px',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '9999px',
                minWidth: '18px',
                textAlign: 'center'
              }}>
                {totalOpenFindings}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('runs')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'runs' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'runs' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <PlayCircle size={16} />
            Runs
          </button>
          <button
            onClick={() => setActiveTab('chat-agents')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'chat-agents' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'chat-agents' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <Bot size={16} />
            Chat Agents
          </button>
          <button
            onClick={() => setActiveTab('agents-md')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'agents-md' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'agents-md' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <FileCode size={16} />
            AGENTS.md
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'product' && renderProductTab()}
        {activeTab === 'organization' && renderOrganizationTab()}
        {activeTab === 'findings' && renderFindingsTab()}
        {activeTab === 'runs' && renderRunsTab()}
        {activeTab === 'chat-agents' && <ChatAgentsTab />}
        {activeTab === 'agents-md' && <AgentsMdTab />}
      </div>
    </div>
  );
}
